import {
  LineOfCreditDetailBanner,
  LineOfCreditInstallmentsData,
  Maybe,
  Money,
  VlocPaymentSchedule,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { mapAlertType } from 'features/lineOfCredit/screens/activeDetailsScreen/helpers/activeDetailsScreen.helpers';
import { storePaymentSchedule } from 'features/lineOfCredit/screens/paymentSchedule/paymentScheduleRouteStore';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLine from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface ActionViewHeaderProps {
  bannerMessage: Maybe<LineOfCreditDetailBanner | null>;
  isAutoPayEnabled: Maybe<boolean | null>;
  paymentSchedule: Maybe<string | null>;
  dueDate: Maybe<string | null>;
  remainingBalance: Maybe<Money | null>;
  paymentDue: Maybe<Money | null>;
  installmentsData: Maybe<LineOfCreditInstallmentsData | null | undefined>;
}

const ActionViewHeader = ({
  bannerMessage,
  isAutoPayEnabled,
  paymentSchedule,
  dueDate,
  remainingBalance,
  paymentDue,
  installmentsData,
}: ActionViewHeaderProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'activeScreen',
  });
  const { segmentClient } = useAnalyticsConfiguration();
  useEffect(() => {
    if (bannerMessage) {
      void segmentClient.logEvent('Banner Shown', {
        source_name: 'vloc_home_page',
        account_type: 'vloc',
        banner_content: bannerMessage.message,
        category: 'vloc',
        subcategory: 'vloc_repayment',
      });
    }
  }, [bannerMessage, segmentClient]);

  function getVLOCPaymentSchedule(): VlocPaymentSchedule[] {
    return installmentsData?.installments.map((installment, index) => ({
      __typename: 'VlocPaymentSchedule',
      id: `${index + 1}` as string,
      amount: installment.amountDue.formattedWithPlusPrefix,
      installmentState: installment.installmentState,
      title: installment.dueDateFormatted,
    })) as VlocPaymentSchedule[];
  }

  const handleMakeAPaymentAction = () => {
    router.push({
      pathname: `./payment-details`,
    });
  };

  const handlePaymentScheduleAction = () => {
    router.push({
      pathname: '/varo-line-of-credit/payment-schedule',
      params: {
        paymentScheduleId: storePaymentSchedule(getVLOCPaymentSchedule()),
        paidToDate: installmentsData?.paidToDate?.formattedWithPlusPrefix,
        totalAmount:
          installmentsData?.remainingBalance?.formattedWithPlusPrefix,
        subcategory: 'vloc_repayment',
      },
    });
  };

  const handleAutoPayNavigation = () => {
    router.navigate({
      pathname: '/varo-line-of-credit/autopay',
      params: {
        autoPayEnabled: isAutoPayEnabled?.toString(),
      },
    });
  };

  return (
    <View>
      {bannerMessage ? (
        <View style={styles.bannerWrapper}>
          <InlineAlert
            titleText={bannerMessage.title}
            bodyText={bannerMessage.message}
            alertType={mapAlertType(bannerMessage.bannerStatus)}
          />
        </View>
      ) : null}
      <View style={styles.balanceContainer}>
        <BodySmBoldText data-testId="balanceTitle" numberOfLines={0}>
          {translatedText('amountDue')}
        </BodySmBoldText>
        {paymentDue?.formattedWithPlusPrefix ? (
          <ExpressiveSmText data-testId="balance" numberOfLines={0}>
            {paymentDue.formattedWithPlusPrefix}
          </ExpressiveSmText>
        ) : null}
      </View>
      <ListItem
        primaryCopy={translatedText('dueDate')}
        showsChevron={false}
        trailingAccessory={{
          id: 'dueDate',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: dueDate || '',
          },
        }}
      />
      <View style={styles.hairlineWrapper}>
        <HairLine />
      </View>
      <ListItem
        primaryCopy={translatedText('autoPay')}
        showsChevron={!isAutoPayEnabled}
        onPress={!isAutoPayEnabled ? handleAutoPayNavigation : null}
        analyticsId="autoPayClicked"
        customAttributes={{
          autopay_status: isAutoPayEnabled ? 'on' : 'off',
          account_type: 'vloc',
        }}
        trailingAccessory={{
          id: 'vloc-manage-account-autopay-tag',
          name: VXLComponentId.TAG,
          props: {
            text: isAutoPayEnabled
              ? translatedText('autoPayStatus.on')
              : translatedText('autoPayStatus.off'),
            type: isAutoPayEnabled ? TagType.SUCCESS : TagType.ERROR,
          },
        }}
      />
      <View style={styles.buttonWrapper}>
        <Button
          label={translatedText('makePayment')}
          onPress={handleMakeAPaymentAction}
          analyticsId="makePaymentClicked"
          customAttributes={{ cta: 'Make a payment', account_type: 'vloc' }}
        />
      </View>
      <View style={styles.sectionDivider} />
      <ListItem
        primaryCopy={translatedText('remainingBalance')}
        showsChevron={false}
        trailingAccessory={{
          id: 'remainingBalance',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: remainingBalance?.formattedWithPlusPrefix || '',
          },
        }}
      />
      <View style={styles.hairlineWrapper}>
        <HairLine />
      </View>
      <ListItem
        primaryCopy={translatedText('paymentSchedule')}
        showsChevron
        onPress={handlePaymentScheduleAction}
        analyticsId="paymentScheduleClicked"
        trailingAccessory={{
          id: 'paymentSchedule',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: paymentSchedule || '',
          },
        }}
      />
      <View style={styles.sectionDivider} />
    </View>
  );
};

const styles = StyleSheet.create({
  balanceContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: 4,
    margin: theme.spacing.m,
  },
  bannerWrapper: {
    margin: theme.spacing.m,
  },
  buttonWrapper: {
    marginBottom: theme.spacing.m,
    marginHorizontal: theme.spacing.s,
    marginTop: theme.spacing.s,
  },
  hairlineWrapper: {
    marginHorizontal: theme.spacing.m,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.secondary,
    height: theme.spacing.s,
  },
});

export default ActionViewHeader;
